/**
 * FilterSidebar - Desktop Filter Panel (Deduplicated)
 * 
 * Redesign 2026:
 * - REMOVED "Lieferung" toggle (already in DeliveryModeToggle header)
 * - REMOVED "Geöffnet" toggle (already in QuickFilterChips)
 * - Kept: Ernährung, Preisniveau, Küche, Entfernung
 * - Cleaner section dividers, less visual noise
 * - Slimmer 260px width
 */

import React, { useState, startTransition } from 'react';
import {
  ChevronDown, MapPin, RotateCcw, Leaf, Euro
} from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { useFilterStore } from '@/lib/FilterStore';
import type { Cuisine } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useCuisines } from '@/hooks/useCuisines';
import { useSkills } from '@/hooks/useSkills';

/* ─── Accordion Section ─── */
const Section: React.FC<{
  title: string;
  icon?: React.ReactNode;
  badge?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, badge, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100/80 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between py-3.5 group"
      >
        <span className="flex items-center gap-2">
          {icon && <span className="text-gray-400">{icon}</span>}
          <span className="text-[13px] font-semibold text-gray-700">
            {title}
          </span>
          {!!badge && badge > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#009544] text-white text-[10px] font-bold">
              {badge}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            "w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-transform duration-150",
            open && "rotate-180"
          )}
        />
      </button>
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-150",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className="pb-4">{children}</div>
        </div>
      </div>
    </div>
  );
};

/* ─── Pill ─── */
const Pill: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "h-8 px-3 rounded-lg text-[13px] font-medium transition-colors",
      "border focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009544]/20",
      active
        ? "bg-[#009544] text-white border-[#009544]"
        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
    )}
  >
    {children}
  </button>
);

/* ─── Main Component ─── */
interface FilterSidebarProps {
  cuisinesOverride?: Cuisine[];
  isLoadingCuisinesOverride?: boolean;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  cuisinesOverride,
  isLoadingCuisinesOverride
}) => {
  const filterStore = useFilterStore();
  const [showAllCuisines, setShowAllCuisines] = useState(false);

  const { data: cuisines = [], isLoading: isLoadingCuisines } = useCuisines();
  const { data: skills = [], isLoading: isLoadingSkills } = useSkills();

  const effectiveCuisines = cuisinesOverride?.length ? cuisinesOverride : cuisines;
  const isCuisinesLoading = isLoadingCuisinesOverride ?? isLoadingCuisines;

  const sortedCuisines = effectiveCuisines
    .slice()
    .sort((a, b) => (a.sort_order ?? a.order ?? 0) - (b.sort_order ?? b.order ?? 0));

  const displayedCuisines = showAllCuisines ? sortedCuisines : sortedCuisines.slice(0, 8);

  const dietarySkills = skills.filter(skill => {
    const n = (skill.name || '').toLowerCase();
    return ['vegan', 'vegetar', 'veggie', 'gluten', 'laktose', 'lactose', 'milchfrei',
            'bio', 'halal', 'koscher', 'keto', 'paleo', 'low carb', 'dairy'].some(k => n.includes(k));
  });

  const toggleCuisine = (id: number) => {
    const cur = filterStore.cuisineIds;
    startTransition(() => {
      filterStore.setCuisines(cur.includes(id) ? cur.filter(c => c !== id) : [...cur, id]);
    });
  };

  const toggleSkill = (id: number) => {
    const cur = filterStore.skills;
    startTransition(() => {
      filterStore.setSkills(cur.includes(id) ? cur.filter(s => s !== id) : [...cur, id]);
    });
  };

  const activeCount = filterStore.getActiveFiltersCount();
  const hasLocation = !!(filterStore.location?.lat && filterStore.location?.lng);

  return (
    <aside
      id="filter-sidebar"
      className="w-full rounded-xl border border-gray-200/60 overflow-hidden bg-white"
    >
      {/* Header */}
      <div className="px-4 py-3.5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-gray-800">Filter</h2>
            {activeCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#009544] text-white text-[10px] font-bold">
                {activeCount}
              </span>
            )}
          </div>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={() => startTransition(() => filterStore.resetFilters())}
              className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-[#009544] transition-colors">
              <RotateCcw className="w-3 h-3" />
              Zurücksetzen
            </button>
          )}
        </div>
      </div>

      <div className="px-4 pb-3">
        {/* ── Ernährung ── */}
        <Section
          title="Ernährung"
          icon={<Leaf className="w-3.5 h-3.5" />}
          badge={filterStore.skills.length}
          defaultOpen={true}
        >
          {isLoadingSkills ? (
            <div className="flex gap-2 flex-wrap">
              {[56, 64, 48].map((w, i) => (
                <div key={i} className="h-8 rounded-lg bg-gray-100 animate-pulse" style={{ width: w }} />
              ))}
            </div>
          ) : dietarySkills.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {dietarySkills.map(skill => (
                <Pill
                  key={skill.id}
                  active={filterStore.skills.includes(skill.id)}
                  onClick={() => toggleSkill(skill.id)}
                >
                  {skill.name}
                </Pill>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-2">
              Keine Optionen
            </p>
          )}
        </Section>

        {/* ── Preisniveau ── */}
        <Section
          title="Preisniveau"
          icon={<Euro className="w-3.5 h-3.5" />}
          badge={filterStore.priceLevel ? 1 : 0}
          defaultOpen={true}
        >
          <div className="flex gap-1.5">
            {(['€', '€€', '€€€', '€€€€'] as const).map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => startTransition(() => 
                  filterStore.setPriceLevel(filterStore.priceLevel === level ? null : level)
                )}
                className={cn(
                  "flex-1 h-9 rounded-lg text-[13px] font-semibold transition-colors",
                  "border focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009544]/20",
                  filterStore.priceLevel === level
                    ? "bg-[#009544] text-white border-[#009544]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                )}
                title={
                  level === '€' ? 'Günstig' :
                  level === '€€' ? 'Mittel' :
                  level === '€€€' ? 'Gehoben' : 'Premium'
                }
              >
                {level}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-gray-400">
            {filterStore.priceLevel === '€' && 'Günstige Restaurants'}
            {filterStore.priceLevel === '€€' && 'Mittlere Preisklasse'}
            {filterStore.priceLevel === '€€€' && 'Gehobene Küche'}
            {filterStore.priceLevel === '€€€€' && 'Premium & Fine Dining'}
            {!filterStore.priceLevel && 'Alle Preisklassen'}
          </p>
        </Section>

        {/* ── Küche ── */}
        <Section
          title="Küche"
          badge={filterStore.cuisineIds.length}
        >
          {isCuisinesLoading ? (
            <div className="flex gap-2 flex-wrap">
              {[56, 48, 64, 52, 44].map((w, i) => (
                <div key={i} className="h-8 rounded-lg bg-gray-100 animate-pulse" style={{ width: w }} />
              ))}
            </div>
          ) : displayedCuisines.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {displayedCuisines.map(cuisine => (
                <Pill
                  key={cuisine.id}
                  active={filterStore.cuisineIds.includes(cuisine.id)}
                  onClick={() => toggleCuisine(cuisine.id)}
                >
                  {cuisine.name}
                </Pill>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-2">
              Keine Küchen
            </p>
          )}

          {sortedCuisines.length > 8 && (
            <button
              type="button"
              onClick={() => setShowAllCuisines(!showAllCuisines)}
              className="mt-2 text-xs font-medium text-[#009544] hover:text-[#007538] transition-colors"
            >
              {showAllCuisines ? 'Weniger anzeigen' : `+${sortedCuisines.length - 8} mehr`}
            </button>
          )}
        </Section>

        {/* ── Entfernung ── */}
        <Section
          title="Entfernung"
          icon={<MapPin className="w-3.5 h-3.5" />}
          defaultOpen={hasLocation}
        >
          {hasLocation ? (
            <div className="space-y-3">
              <Slider
                value={[filterStore.distance]}
                min={1}
                max={20}
                step={1}
                onValueChange={values => filterStore.setDistance(values[0])}
                className="h-2"
              />
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-gray-800">{filterStore.distance} km</span>
                <span className="text-gray-400">Max 20 km</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 py-1.5">
              Standort eingeben für Entfernungsfilter
            </p>
          )}
        </Section>
      </div>
    </aside>
  );
};

export default FilterSidebar;
